import { formatAmount } from '../utils/format.js';
import PropTypes from 'prop-types';
import { NATIONAL_AVERAGES_USD } from '../data/countries.js';

const BUDGET_CATEGORIES = [
  { key: 'needs', label: 'Needs', pct: 50, color: '#8be9fd', icon: '🏠', desc: 'Rent, groceries, utilities, transport' },
  { key: 'wants', label: 'Wants', pct: 30, color: '#bd93f9', icon: '🎭', desc: 'Dining, entertainment, hobbies' },
  { key: 'savings', label: 'Savings', pct: 20, color: '#50fa7b', icon: '💰', desc: 'Investments, emergency fund, goals' },
];

export default function SalaryInsights({ result, currency, country, displayCurrency, convert }) {
  if (!result || !result.net) return null;

  const netMonthly = result.net / 12;

  const conv = (amt) => {
    if (displayCurrency && displayCurrency !== currency && convert) {
      return convert(amt, currency, displayCurrency);
    }
    return amt;
  };
  const displayCurr = displayCurrency || currency;

  // Compare with national average
  const avgUSD = NATIONAL_AVERAGES_USD[country];
  const grossUSD = convert ? convert(result.gross, currency, 'USD') : result.gross;
  const aboveAverage = avgUSD ? ((grossUSD - avgUSD) / avgUSD) * 100 : null;

  return (
    <div className="salary-insights">
      <h3 className="salary-insights__title">
        <span className="salary-insights__title-icon">💡</span>
        Financial Insights
      </h3>

      {/* National average comparison */}
      {aboveAverage !== null && (
        <div className={`salary-insights__avg ${aboveAverage >= 0 ? 'above' : 'below'}`}>
          <div className="salary-insights__avg-icon">{aboveAverage >= 0 ? '📈' : '📉'}</div>
          <div className="salary-insights__avg-text">
            <strong>
              {Math.abs(aboveAverage).toFixed(0)}% {aboveAverage >= 0 ? 'above' : 'below'} national average
            </strong>
            <span>vs. estimated national avg of {formatAmount(conv(convert ? convert(avgUSD, 'USD', currency) : avgUSD), displayCurr)}/yr</span>
          </div>
        </div>
      )}

      {/* 50/30/20 Budget Rule */}
      <div className="salary-insights__budget">
        <h4 className="salary-insights__budget-title">Monthly Budget Breakdown <span className="salary-insights__badge">50/30/20 Rule</span></h4>
        <div className="salary-insights__budget-items">
          {BUDGET_CATEGORIES.map(({ key, label, pct, color, icon, desc }) => {
            const amount = (netMonthly * pct) / 100;
            return (
              <div key={key} className="salary-insights__budget-item">
                <div className="salary-insights__budget-header">
                  <span className="salary-insights__budget-icon">{icon}</span>
                  <span className="salary-insights__budget-label">{label}</span>
                  <span className="salary-insights__budget-pct" style={{ color }}>{pct}%</span>
                  <span className="salary-insights__budget-amount">{formatAmount(conv(amount), displayCurr)}/mo</span>
                </div>
                <div className="salary-insights__budget-bar-track">
                  <div className="salary-insights__budget-bar-fill" style={{ width: `${pct}%`, background: color }} />
                </div>
                <span className="salary-insights__budget-desc">{desc}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick stats */}
      <div className="salary-insights__stats">
        <div className="salary-insights__stat">
          <span className="salary-insights__stat-icon">⏱</span>
          <span className="salary-insights__stat-label">Daily earnings</span>
          <strong className="salary-insights__stat-value">{formatAmount(conv(result.net / 260), displayCurr)}</strong>
        </div>
        <div className="salary-insights__stat">
          <span className="salary-insights__stat-icon">☕</span>
          <span className="salary-insights__stat-label">Per working hour</span>
          <strong className="salary-insights__stat-value">{formatAmount(conv(result.net / 2080), displayCurr)}</strong>
        </div>
        <div className="salary-insights__stat">
          <span className="salary-insights__stat-icon">🧾</span>
          <span className="salary-insights__stat-label">Effective tax rate</span>
          <strong className="salary-insights__stat-value">{result.effectiveRate.toFixed(1)}%</strong>
        </div>
      </div>
    </div>
  );
}

SalaryInsights.propTypes = {
  result: PropTypes.shape({
    net:           PropTypes.number.isRequired,
    gross:         PropTypes.number.isRequired,
    effectiveRate: PropTypes.number.isRequired,
  }).isRequired,
  currency:        PropTypes.string.isRequired,
  country:         PropTypes.string.isRequired,
  displayCurrency: PropTypes.string,
  convert:         PropTypes.func,
}

SalaryInsights.defaultProps = {
  displayCurrency: undefined,
  convert:         undefined,
}